import { z } from 'zod';

/**
 * O fio do agendamento de carro FORA do chat (bancada, #6797): `/mobility/schedule/*` do
 * app-integration ↔ web-chat. Os dois lados importam daqui. Erros saem `{ error: <código> }`
 * (ver `MobilityScheduleErrorCode`).
 */

const Latitude = z.number().min(-90).max(90);
const Longitude = z.number().min(-180).max(180);
/** Data e hora LOCAIS do embarque, sem fuso (`YYYY-MM-DDTHH:mm`), como o legado manda. */
const LocalDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d$/);
const Money = z.number().nonnegative();

/** Um ponto da corrida. Mesmo shape dos recentes do customer-api (`mobility-recent-places`). */
export const MobilitySchedulePlaceSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().max(300).optional(),
    /** O id do voll-places, quando o ponto veio da busca. */
    ref: z.string().min(1).max(512).optional(),
    latitude: Latitude,
    longitude: Longitude,
  })
  .strict();
export type MobilitySchedulePlace = z.infer<typeof MobilitySchedulePlaceSchema>;

/** `GET /mobility/schedule/options`: o catálogo da busca. `franchises` são os pacotes do carro
 *  com motorista (a chave vai no `franchiseKey`; `'all'` = todos); `vehicleTypes`, as
 *  categorias. `services` = os serviços liberados para quem pede. */
export const MobilityScheduleOptionsSchema = z
  .object({
    services: z.object({ cooperative: z.boolean(), carDriver: z.boolean() }).strict(),
    stopQuantity: z.number().int().nonnegative(),
    franchises: z.array(
      z
        .object({ key: z.string().min(1), hours: z.number().nonnegative(), km: z.number() })
        .strict(),
    ),
    vehicleTypes: z.array(z.object({ id: z.string().min(1), name: z.string().min(1) }).strict()),
  })
  .strict();
export type MobilityScheduleOptions = z.infer<typeof MobilityScheduleOptionsSchema>;

/** `GET /mobility/schedule/places/recent`: os recentes de quem pede, mais novo primeiro. */
export const MobilityScheduleRecentPlacesSchema = z
  .object({ places: z.array(MobilitySchedulePlaceSchema) })
  .strict();

/** `GET /mobility/schedule/traveler-phone[?travelerId=]`: o telefone do cadastro do viajante
 *  (sem `travelerId`, o de quem pede). Só PREENCHE o campo: nada no BFF grava telefone. */
export const MobilityScheduleTravelerPhoneSchema = z
  .object({ phoneNumber: z.string().nullable() })
  .strict();

/** O viajante: ausente = quem pede. Id legado do colega no cliente (o mesmo do Quem). */
const TravelerId = z.string().trim().min(1).max(64).optional();

/** A política de distância da cooperativa, conferida com o VIAJANTE. Não bloqueia: fora do
 *  limite, o pedido exige justificativa e pode ir para aprovação. `routeKm` = o km da rota.
 *  `limitKm` = o limite configurado na política do viajante (policy-api); `null` = sem limite,
 *  ausente = a leitura do limite falhou. `null` na resposta = a consulta falhou; o `bookings`
 *  confere de novo. */
export const MobilityScheduleDistancePolicySchema = z
  .object({
    withinLimit: z.boolean(),
    routeKm: z.number().nonnegative(),
    limitKm: z.number().nonnegative().nullable().optional(),
  })
  .strict();
export type MobilityScheduleDistancePolicy = z.infer<typeof MobilityScheduleDistancePolicySchema>;

/** `POST /mobility/schedule/cooperatives`. `roundTrip`: a volta é a rota invertida. */
export const MobilityScheduleCooperativesRequestSchema = z
  .object({
    travelerId: TravelerId,
    from: MobilitySchedulePlaceSchema,
    to: MobilitySchedulePlaceSchema,
    roundTrip: z.boolean().optional(),
  })
  .strict();
export type MobilityScheduleCooperativesRequest = z.infer<
  typeof MobilityScheduleCooperativesRequestSchema
>;

/** Uma cooperativa. Na ida e volta, `returnPrice` é o preço da rota invertida e `totalPrice`
 *  soma os dois; a que não faz a volta (`validReturn: false`) vem com `returnPrice: null` e não
 *  serve para ida e volta. Preço ESTIMADO: o final é a cooperativa que confirma. */
export const MobilityScheduleCooperativeSchema = z
  .object({
    cooperativeId: z.string().min(1),
    name: z.string().min(1),
    outwardPrice: Money,
    returnPrice: Money.nullable(),
    totalPrice: Money,
    validReturn: z.boolean(),
  })
  .strict();
export type MobilityScheduleCooperative = z.infer<typeof MobilityScheduleCooperativeSchema>;

export const MobilityScheduleCooperativesResponseSchema = z
  .object({
    cooperatives: z.array(MobilityScheduleCooperativeSchema),
    distancePolicy: MobilityScheduleDistancePolicySchema.nullable(),
  })
  .strict();
export type MobilityScheduleCooperativesResponse = z.infer<
  typeof MobilityScheduleCooperativesResponseSchema
>;

/** `POST /mobility/schedule/car-driver/offers`. `franchiseKey`: `'all'` ou `'{N}H'`. */
export const MobilityScheduleCarDriverRequestSchema = z
  .object({
    travelerId: TravelerId,
    from: MobilitySchedulePlaceSchema,
    franchiseKey: z.string().trim().min(1).max(16),
    vehicleTypeIds: z.array(z.string().min(1)).max(20).optional(),
    bilingual: z.boolean().optional(),
    overnight: z.boolean().optional(),
  })
  .strict();
export type MobilityScheduleCarDriverRequest = z.infer<
  typeof MobilityScheduleCarDriverRequestSchema
>;

/** Uma tarifa de carro com motorista. `dailyPrice` é POR diária; os extras, por unidade
 *  (bilíngue por diária, pernoite por noite) e `null` quando a tarifa não os oferece. */
export const MobilityScheduleCarDriverOfferSchema = z
  .object({
    cooperativeId: z.string().min(1),
    tariffId: z.string().min(1),
    /** O pacote DESTA tarifa (`'{N}H'`; sem horas, o filtro da busca): o que vai no
     *  `franchiseKey` do `bookings`. */
    franchiseKey: z.string().min(1),
    name: z.string().min(1),
    vehicleName: z.string().nullable(),
    hours: z.number().nonnegative(),
    km: z.number(),
    dailyPrice: Money,
    bilingualDailyPrice: Money.nullable(),
    overnightPrice: Money.nullable(),
  })
  .strict();
export type MobilityScheduleCarDriverOffer = z.infer<typeof MobilityScheduleCarDriverOfferSchema>;

export const MobilityScheduleCarDriverResponseSchema = z
  .object({ offers: z.array(MobilityScheduleCarDriverOfferSchema) })
  .strict();

const BookingBase = {
  travelerId: TravelerId,
  from: MobilitySchedulePlaceSchema,
  /** Ida (ou início do carro com motorista), no relógio local do embarque. */
  scheduleAt: LocalDateTime,
  cooperativeId: z.string().min(1),
  /** Recado à cooperativa (voo, ponto de encontro…). */
  note: z.string().trim().max(500).optional(),
  /** A viagem em foco (de onde a bancada abriu); a posse é conferida no servidor. */
  tripId: z.string().min(1).optional(),
  /** O submit do checkout (`CheckoutSubmit` do genui-contract, produto `mobility`): rateio,
   *  motivo, justificativa, observação e, no bloco do viajante, `edits.phone`/`countryCode` —
   *  o telefone DESTA corrida, que nunca é gravado em perfil algum. Validado no BFF. */
  form: z.unknown(),
};

export const MobilityScheduleBookingRequestSchema = z.discriminatedUnion('service', [
  z
    .object({
      service: z.literal('cooperative'),
      ...BookingBase,
      to: MobilitySchedulePlaceSchema,
      stopOvers: z.array(MobilitySchedulePlaceSchema).max(10).optional(),
      /** Presente = ida e volta: a volta sai do destino para a origem, na rota invertida. */
      returnAt: LocalDateTime.optional(),
      companions: z.number().int().min(0).max(3).optional(),
    })
    .strict(),
  z
    .object({
      service: z.literal('car_driver'),
      ...BookingBase,
      tariffId: z.string().min(1),
      franchiseKey: z.string().trim().min(1).max(16),
      days: z.number().int().min(1).max(30),
      bilingual: z.boolean().optional(),
      overnightDays: z.number().int().min(0).max(29).optional(),
    })
    .strict(),
]);
export type MobilityScheduleBookingRequest = z.infer<typeof MobilityScheduleBookingRequestSchema>;

/** Um trecho criado: a ida e, na ida e volta, a volta. `estimate` = o preço que o servidor
 *  recalculou (o do cliente nunca vale). `scheduleId` da volta é `null` quando a leitura logo
 *  após criar falhou (a volta existe; o id chega pelo voucher). */
export const MobilityScheduleLegSchema = z
  .object({
    scheduleId: z.string().min(1).nullable(),
    scheduleAt: LocalDateTime,
    from: z.string(),
    to: z.string().nullable(),
    estimate: Money,
  })
  .strict();

/** `POST /mobility/schedule/bookings` (202). `outcome`: `scheduled` (agendada) ou
 *  `pending_approval` (enviada para aprovação); `null` = o status não pôde ser lido logo após
 *  criar. `tripId` = a viagem onde a corrida ficou, quando o trip-api respondeu. */
export const MobilityScheduleBookingRequestedSchema = z
  .object({
    status: z.literal('requested'),
    outcome: z.enum(['scheduled', 'pending_approval']).nullable(),
    tripId: z.string().nullable(),
    legs: z.array(MobilityScheduleLegSchema).min(1),
  })
  .strict();

/** 202 sem saber se nasceu (rede/5xx no create): a tela manda conferir a viagem antes de
 *  tentar de novo. */
export const MobilityScheduleBookingIndeterminateSchema = z
  .object({ status: z.literal('indeterminate') })
  .strict();

export const MobilityScheduleBookingResponseSchema = z.union([
  MobilityScheduleBookingRequestedSchema,
  MobilityScheduleBookingIndeterminateSchema,
]);
export type MobilityScheduleBookingResponse = z.infer<typeof MobilityScheduleBookingResponseSchema>;

/** `GET /mobility/schedule/vouchers/:voucherId`: o bloco do drawer. `cancellable` = as três
 *  travas do cancelamento passam agora. */
export const MobilityScheduleVoucherSchema = z
  .object({
    voucherId: z.string().min(1),
    voucherCode: z.string().nullable(),
    status: z.string().nullable(),
    scheduleType: z.string().nullable(),
    scheduleDate: z.string().nullable(),
    origin: z.string().nullable(),
    destination: z.string().nullable(),
    stopOvers: z.array(z.string()),
    estimatedValue: z.number().nullable(),
    justificationPrice: z.string().nullable(),
    steps: z.array(
      z
        .object({ status: z.string(), active: z.boolean(), updatedAt: z.string().nullable() })
        .strict(),
    ),
    driver: z
      .object({
        name: z.string().nullable(),
        phone: z.string().nullable(),
        carPlate: z.string().nullable(),
        model: z.string().nullable(),
        maker: z.string().nullable(),
        color: z.string().nullable(),
      })
      .strict()
      .nullable(),
    cancellable: z.boolean(),
  })
  .strict();
export type MobilityScheduleVoucher = z.infer<typeof MobilityScheduleVoucherSchema>;

/** `POST /mobility/schedule/vouchers/:voucherId/cancel` (200). */
export const MobilityScheduleCancelResponseSchema = z
  .object({ status: z.literal('cancelled') })
  .strict();

/** Os `error` das rotas `/mobility/schedule/*` além dos do gate de produto
 *  (`identity_not_found`, `product_not_enabled`, `policy_unavailable`). */
export const MOBILITY_SCHEDULE_ERROR_CODES = [
  'invalid_body',
  'invalid_query',
  /** Nenhum serviço do agendamento liberado (flags, política ou gastos bloqueados). */
  'mobility_schedule_not_enabled',
  /** O serviço pedido (cooperativa ou carro com motorista) não está liberado. */
  'service_not_enabled',
  /** O viajante não é do cliente de quem pede. */
  'traveler_not_allowed',
  /** 400 com `field`: o checkout incompleto (rateio, motivo, telefone…). */
  'missing_required',
  /** 422 com `earliest` (`YYYY-MM-DDTHH:mm`): abaixo da antecedência mínima. */
  'lead_time',
  /** 422: a volta não é depois da ida. */
  'return_before_outward',
  /** 422: fora do limite de km sem justificativa da lista. */
  'justification_required',
  /** 409: a cooperativa/tarifa escolhida não volta mais na busca (ou não faz a volta). */
  'option_unavailable',
  'budget_insufficient',
  'budget_unavailable',
  /** 503: a política de distância não pôde ser lida no envio. */
  'distance_policy_unavailable',
  /** 409: outro envio do mesmo pedido em curso. */
  'booking_in_progress',
  /** 422 com `message`: o agendamento recusou (a mensagem do servidor). */
  'schedule_refused',
  'invalid_trip',
  /** 404: não é um agendamento (corrida da 99, outro produto) ou não é de quem pede. */
  'not_schedule',
  /** 409: o status mudou e não se cancela mais por aqui. */
  'not_cancellable',
  'mobility_schedule_unavailable',
] as const;
export type MobilityScheduleErrorCode = (typeof MOBILITY_SCHEDULE_ERROR_CODES)[number];
